import React from 'react';
import { useHistory } from 'react-router-dom';
import Api from 'api';
import DentistNavbar from 'components/DentistNavbar';
import {
  CeDirectory,
  type CeDirectorySafeLocation,
} from '../../../features/ceDirectory';
import { useOfficeCeDirectoryAccess } from './useOfficeCeDirectoryAccess';
import {
  clearSavedOfficeCeLocation,
  initializeOfficeCeLocation,
  saveOfficeCeLocation,
  toOfficeCeLocations,
  toOfficeCeLocationsFromSafeList,
  type OfficeCeLocation,
  type OfficeLocationSelection,
  type OfficeLocationSource,
} from './locationSelection';
import styles from './OfficeCeCoursesPage.module.scss';

type LocationInvalidationEvent = {
  reason: 'selection_required' | 'access_denied';
  locations?: CeDirectorySafeLocation[];
};

const getStorage = (): Storage => window.localStorage;

const locationContext = (location: OfficeCeLocation): string => (
  [location.city, location.provinceState].filter(Boolean).join(', ')
);

const selectedResult = (
  locations: OfficeCeLocation[],
  selectedLocation: OfficeCeLocation,
): OfficeLocationSelection => ({
  status: 'selected',
  locations,
  selectedLocation,
  selectionSource: 'saved',
  staleSelectionCleared: false,
});

const OfficeLocationPicker = ({
  locations,
  onSelect,
}: {
  locations: OfficeCeLocation[];
  onSelect: (locationId: number) => void;
}) => (
  <section className={styles.picker} aria-labelledby="office-ce-location-heading">
    <div className={styles.pickerIntro}>
      <p className={styles.eyebrow}>Office location</p>
      <h2 id="office-ce-location-heading">Choose an office</h2>
      <p>Your choice sets the course location and is used for View course attribution.</p>
    </div>
    <div className={styles.locationGrid}>
      {locations.map(location => {
        const context = locationContext(location);
        return (
          <button
            type="button"
            className={styles.locationChoice}
            key={location.id}
            onClick={() => onSelect(location.id)}
          >
            <span className={styles.locationName}>{location.label}</span>
            {context && <span className={styles.locationContext}>{context}</span>}
            <span className={styles.locationAction}>Use this location</span>
          </button>
        );
      })}
    </div>
  </section>
);

const OfficeCeCoursesPage: React.FC = () => {
  const history = useHistory();
  const access = useOfficeCeDirectoryAccess();
  const [selection, setSelection] = React.useState<OfficeLocationSelection | null>(null);
  const [loadingLocations, setLoadingLocations] = React.useState(false);
  const [locationError, setLocationError] = React.useState(false);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const requestVersion = React.useRef(0);

  React.useEffect(() => {
    if (!access.eligible || access.availability === false) {
      history.replace({ pathname: 'dentistHome' });
    }
  }, [access.availability, access.eligible, history]);

  const loadLocations = React.useCallback(async (forceExplicitChoice = false) => {
    if (access.userId == null) return;
    const version = ++requestVersion.current;
    setLoadingLocations(true);
    setLocationError(false);
    if (forceExplicitChoice) setSelection(null);
    try {
      const response = await Api.getValidLocations();
      if (version !== requestVersion.current) return;
      const locations = toOfficeCeLocations(response as OfficeLocationSource[]);
      const next = initializeOfficeCeLocation(
        getStorage(),
        access.userId,
        locations,
        { forceExplicitChoice },
      );
      setSelection(next);
      setPickerOpen(next.status === 'selection_required');
    } catch {
      if (version !== requestVersion.current) return;
      setSelection(null);
      setLocationError(true);
    } finally {
      if (version === requestVersion.current) setLoadingLocations(false);
    }
  }, [access.userId]);

  React.useEffect(() => {
    if (!access.enabled || access.userId == null) return undefined;
    void loadLocations();
    return () => {
      requestVersion.current += 1;
    };
  }, [access.enabled, access.userId, loadLocations]);

  const chooseLocation = React.useCallback((locationId: number) => {
    if (access.userId == null || !selection) return;
    const selectedLocation = selection.locations.find(location => location.id === locationId);
    if (!selectedLocation) return;
    saveOfficeCeLocation(getStorage(), access.userId, selectedLocation.id);
    setSelection(selectedResult(selection.locations, selectedLocation));
    setPickerOpen(false);
  }, [access.userId, selection]);

  const handleLocationContextInvalid = React.useCallback((event: LocationInvalidationEvent) => {
    if (access.userId == null) return;
    clearSavedOfficeCeLocation(getStorage(), access.userId);
    setPickerOpen(true);

    if (event.locations !== undefined) {
      const locations = toOfficeCeLocationsFromSafeList(event.locations);
      const next = initializeOfficeCeLocation(
        getStorage(),
        access.userId,
        locations,
        { forceExplicitChoice: true },
      );
      setSelection(next);
      setLocationError(false);
      return;
    }

    void loadLocations(true);
  }, [access.userId, loadLocations]);

  const handleAvailabilityChange = React.useCallback((enabled: boolean) => {
    if (!enabled) history.replace({ pathname: 'dentistHome' });
  }, [history]);

  if (!access.enabled) return null;

  const showPicker = selection?.status === 'selection_required'
    || (pickerOpen && selection?.status === 'selected');
  const selectedLocation = selection?.status === 'selected'
    ? selection.selectedLocation
    : null;

  return (
    <main className={styles.page}>
      <DentistNavbar title="CE Courses" showGetTempButton={false} />
      <div className={styles.content}>
        {loadingLocations && (
          <div className={styles.status} role="status" aria-live="polite">
            Loading office locations…
          </div>
        )}

        {!loadingLocations && locationError && (
          <section className={styles.status} role="alert">
            <h2>We could not load your office locations</h2>
            <p>Please try again before browsing CE courses.</p>
            <button type="button" onClick={() => void loadLocations()}>Retry</button>
          </section>
        )}

        {!loadingLocations && !locationError && selection?.status === 'empty' && (
          <section className={styles.status}>
            <h2>No active office locations are available</h2>
            <p>Add or reactivate a Location before browsing CE courses.</p>
            <button
              type="button"
              onClick={() => {
                const goReact = (window as any).TempStars?.App?.goReact;
                if (typeof goReact === 'function') goReact('dentistLocations');
                else history.push({ pathname: 'dentistLocations' });
              }}
            >
              View Locations
            </button>
          </section>
        )}

        {!loadingLocations && !locationError && showPicker && selection && (
          <OfficeLocationPicker locations={selection.locations} onSelect={chooseLocation} />
        )}

        {!loadingLocations && !locationError && selectedLocation && !showPicker && (
          <>
            <section className={styles.selectedLocation} aria-label="Selected office location">
              <div>
                <span className={styles.selectedLabel}>Courses for</span>
                <strong>{selectedLocation.label}</strong>
                {locationContext(selectedLocation) && (
                  <span>{locationContext(selectedLocation)}</span>
                )}
              </div>
              {selection?.status === 'selected' && selection.locations.length > 1 && (
                <button type="button" onClick={() => setPickerOpen(true)}>
                  Change location
                </button>
              )}
            </section>
            <CeDirectory
              userId={access.userId as number}
              sourceSurface="office"
              locationId={selectedLocation.id}
              onAvailabilityChange={handleAvailabilityChange}
              onLocationContextInvalid={handleLocationContextInvalid}
            />
          </>
        )}
      </div>
    </main>
  );
};

export default OfficeCeCoursesPage;
